/**
 * projectServiceFactory — the Projects Firestore layer, bound to a collection.
 *
 * Mirrors taskServiceFactory / docsServiceFactory, for the same reason and by
 * the same rule: Projects now exists in more than one space (Work → `projects`,
 * Personal → `personalProjects`), and those are separate COLLECTIONS rather
 * than a `space` field on one, because the collection wall is what makes a
 * private space private (core.md). Binding the collection once means a caller
 * holding a service cannot address another space's data at all.
 *
 * THIS FACTORY INTRODUCES THE FIRST PROJECT WRITE PATH IN THE CODEBASE, and
 * that is worth stating plainly because it is easy to assume otherwise. Before
 * this, `ProjectService` exposed subscribe + a pure progress helper and nothing
 * else; the five live studio projects were all Notion-imported through the
 * Admin SDK, which bypasses rules. There was no create, update or delete
 * anywhere in `src/`, in any space.
 *
 * WORK'S BINDING STAYS READ-ONLY ON PURPOSE — see ProjectService.js. The
 * writers exist here, but the Work service does not expose them, so this
 * refactor cannot change what any existing studio surface can do. `projects` is
 * admin-write in firestore.rules and a studio project is read by the whole
 * team; giving Work a create button is a deliberate separate change with its
 * own decision behind it, not a side effect of making Personal work.
 *
 * PROGRESS IS COMPUTED, NEVER STORED (unchanged, and load-bearing). A stored
 * counter would need updating on every task write in the space and would drift
 * silently the first time one path forgot. `computeProjectProgress` is pure and
 * collection-agnostic, so it is exported once from ProjectService rather than
 * rebound per space.
 */

import { db } from '../firebase';
import {
	collection,
	doc,
	addDoc,
	updateDoc,
	deleteDoc,
	onSnapshot,
	query,
	orderBy,
	serverTimestamp,
} from 'firebase/firestore';

/**
 * @param {object} opts
 * @param {string} opts.projectsCollection the Firestore collection name
 * @param {string} opts.space the space key stamped on new documents
 * @param {boolean} [opts.writable=false] whether this binding exposes writers.
 *   DEFAULTS TO FALSE so a new binding is read-only until someone opts in —
 *   the same restrictive-by-default direction spaceFields takes for unknown
 *   spaces. A permissive default would hand write functions to Work by
 *   omission, which is precisely the change this file must not make.
 */
export function createProjectService({ projectsCollection, space, writable = false }) {
	if (!projectsCollection || !space) {
		// Fail loudly at construction rather than at the first call: a service
		// bound to `undefined` would address a nonsense collection path, and the
		// failure would surface far from its cause.
		throw new Error('createProjectService requires { projectsCollection, space }');
	}

	/**
	 * Subscribe to every project in this space (real-time), title-ascending.
	 * Errors resolve to an EMPTY list rather than throwing, matching the
	 * behaviour the Work module already relied on — a permission denial on a
	 * space the viewer cannot reach renders an empty project list, not a crash.
	 */
	const subscribeToProjects = (callback) => {
		const q = query(collection(db, projectsCollection), orderBy('title', 'asc'));
		return onSnapshot(
			q,
			(snapshot) =>
				callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
			(error) => {
				console.error(`Error subscribing to ${projectsCollection}:`, error);
				callback([]);
			}
		);
	};

	const readOnly = {
		subscribeToProjects,
		// Introspection — lets a test or a debug surface assert which collection a
		// service is bound to without reaching into the closure.
		projectsCollection,
		space,
		writable: false,
	};

	if (!writable) return Object.freeze(readOnly);

	/**
	 * Create a project. `title` is the only required field — a project is a
	 * label tasks point at, and demanding more up front turns a one-second
	 * action into a form.
	 */
	const createProject = async ({ title, description = '', area = null, createdBy = null }) => {
		const clean = String(title || '').trim();
		if (!clean) throw new Error('A project needs a title.');
		const ref = await addDoc(collection(db, projectsCollection), {
			title: clean,
			description,
			area,
			space,
			createdBy,
			createdAt: serverTimestamp(),
			updatedAt: serverTimestamp(),
		});
		return ref.id;
	};

	const updateProject = async (projectId, patch) => {
		await updateDoc(doc(db, projectsCollection, projectId), {
			...patch,
			updatedAt: serverTimestamp(),
		});
	};

	/**
	 * Delete a project document.
	 *
	 * DELIBERATELY DOES NOT TOUCH THE TASKS THAT POINT AT IT. A task's
	 * `projectId` is a reference, and the module already resolves an unknown one
	 * to no project rather than erroring — so a deleted project leaves its tasks
	 * intact and unfiled, which is recoverable. Cascading the delete would
	 * destroy real tasks to remove a label, and there is no undo for that.
	 */
	const deleteProject = async (projectId) => {
		await deleteDoc(doc(db, projectsCollection, projectId));
	};

	return Object.freeze({
		...readOnly,
		writable: true,
		createProject,
		updateProject,
		deleteProject,
	});
}

export default createProjectService;
